import { Show, type Component } from 'solid-js'
import { AppRoutes } from '../../shared/app-routes'
import { useFeature } from '../../shared/feature-flags'

import './Sidebar.module.scss'
import SidebarItem from './SidebarItem'
import OrganizationSelector from './OrganizationSelector'
import { Trans } from '@mbarzda/solid-i18next'
import ChorusLogo from '../../assets/images/chorus-logo.svg'
import { Icon } from '@iconify-icon/solid'

export interface SidebarContentProps {
  readonly onItemSelected: () => void
}

const SidebarContent: Component<SidebarContentProps> = (props) => {
  const isModerationEnabled = useFeature('moderation')

  return (
    <div class="flex grow flex-col gap-y-4 overflow-y-auto bg-white px-6 pb-4 border-r border-gray-200">
      <div class="flex flex-col gap-1 items-start pb-2 pt-5">
        <img class="h-6 w-auto" src={ChorusLogo} alt="Chorus logo" />
        <OrganizationSelector />
      </div>
      <nav class="flex flex-1 flex-col">
        <ul role="list" class="flex flex-1 flex-col gap-y-7">
          <li class="flex flex-col gap-2">
            <ul role="list" class="space-y-1 flex flex-col gap-2">
              <li>
                <SidebarItem
                  url={AppRoutes.Dashboard()}
                  icon={<Icon icon="heroicons:squares-2x2" class="text-[1.5rem]" />}
                  textKey={'dashboard'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>
          <li class="flex flex-col gap-2">
            <div class="text-xs font-semibold leading-4 text-gray-400">
              <Trans key={'ls_sidebar_revenue'} />
            </div>
            <ul role="list" class="space-y-1 flex flex-col gap-1">
              <li>
                <SidebarItem
                  url={AppRoutes.UsersOverview()}
                  icon={<Icon icon="heroicons:chart-bar" class="text-[1.5rem]" />}
                  textKey={'users_overview'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              {/* <li>
                <SidebarItem
                  url={AppRoutes.CashFlow()}
                  icon={<Icon icon="heroicons:currency-dollar" class="text-[1.5rem]" />}
                  textKey={'cash_flow'}
                  onItemSelected={props.onItemSelected}
                />
              </li> */}
              <li>
                <SidebarItem
                  url={AppRoutes.ChurnOverview()}
                  icon={<Icon icon="heroicons:user-minus" class="text-[1.5rem]" />}
                  textKey={'churn_overview'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>

          <li class="flex flex-col gap-2">
            <div class="text-xs font-semibold leading-4 text-gray-400">
              <Trans key={'ls_sidebar_engagement'} />
            </div>
            <ul role="list" class="space-y-1 flex flex-col gap-1">
              <li>
                <SidebarItem
                  url={AppRoutes.Sections()}
                  icon={<Icon icon="heroicons:squares-2x2" class="text-[1.5rem]" />}
                  textKey={'sections'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <li>
                <SidebarItem
                  url={AppRoutes.Songs()}
                  icon={<Icon icon="heroicons:musical-note" class="text-[1.5rem]" />}
                  textKey={'songs'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <li>
                <SidebarItem
                  url={AppRoutes.Videos()}
                  icon={<Icon icon="heroicons:video-camera" class="text-[1.5rem]" />}
                  textKey={'videos'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <li>
                <SidebarItem
                  url={AppRoutes.Events()}
                  icon={<Icon icon="heroicons:ticket" class="text-[1.5rem]" />}
                  textKey={'events'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <li>
                <SidebarItem
                  url={AppRoutes.Community()}
                  icon={<Icon icon="heroicons:chat-bubble-left-right" class="text-[1.5rem]" />}
                  textKey={'community'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <li>
                <SidebarItem
                  url={AppRoutes.Chat()}
                  icon={<Icon icon="heroicons:chat-bubble-oval-left" class="text-[1.5rem]" />}
                  textKey={'chat'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <li>
                <SidebarItem
                  url={AppRoutes.EngagementUsers()}
                  icon={<Icon icon="heroicons:users" class="text-[1.5rem]" />}
                  textKey={'users'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>

          <li class="flex flex-col gap-2">
            <div class="text-xs font-semibold leading-4 text-gray-400">
              <Trans key={'ls_sidebar_database'} />
            </div>
            <ul role="list" class="space-y-1 flex flex-col gap-1">
              <li>
                <SidebarItem
                  url={AppRoutes.UserDirectory()}
                  icon={<Icon icon="heroicons:user-group" class="text-[1.5rem]" />}
                  textKey={'user_directory'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>

          <li class="flex flex-col gap-2">
            <div class="text-xs font-semibold leading-4 text-gray-400">
              <Trans key={'ls_sidebar_reports'} />
            </div>
            <ul role="list" class="space-y-1 flex flex-col gap-1">
              <li>
                <SidebarItem
                  url={AppRoutes.Reports()}
                  icon={<Icon icon="heroicons:clipboard-document-list" class="text-[1.5rem]" />}
                  textKey={'reports'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>

          <li class="flex flex-col gap-2">
            <div class="text-xs font-semibold leading-4 text-gray-400">
              <Trans key={'ls_sidebar_admin_panel'} />
            </div>
            <ul role="list" class="space-y-1 flex flex-col gap-1">
              <li>
                <SidebarItem
                  url={AppRoutes.AdminEvents()}
                  icon={<Icon icon="heroicons:calendar-days-solid" class="text-[1.5rem]" />}
                  textKey={'admin_events'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
              <Show when={isModerationEnabled()}>
                <li>
                  <SidebarItem
                    url={AppRoutes.KeywordFlagging()}
                    icon={<Icon icon="heroicons:language" class="text-[1.5rem]" />}
                    textKey={'keyword_flagging'}
                    onItemSelected={props.onItemSelected}
                  />
                </li>
                <li>
                  <SidebarItem
                    url={AppRoutes.Moderation()}
                    icon={<Icon icon="heroicons:shield-exclamation" class="text-[1.5rem]" />}
                    textKey={'moderation'}
                    onItemSelected={props.onItemSelected}
                  />
                </li>
              </Show>
              <li>
                <SidebarItem
                  url={AppRoutes.Polls()}
                  icon={<Icon icon="heroicons:chart-bar" class="text-[1.5rem]" />}
                  textKey={'polls'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>

          {/* Demographics - commented out for staging/production
          <li class="flex flex-col gap-2">
            <div class="text-xs font-semibold leading-4 text-gray-400">
              <Trans key={'ls_sidebar_demographics'} />
            </div>
            <ul role="list" class="space-y-1 flex flex-col gap-2">
              <li>
                <SidebarItem
                  url={AppRoutes.DemographicsUsers()}
                  icon={<Icon icon="heroicons:user-group" class="text-[1.5rem]" />}
                  textKey={'users'}
                  onItemSelected={props.onItemSelected}
                />
              </li>
            </ul>
          </li>
          */}
        </ul>
      </nav>
    </div>
  )
}

export default SidebarContent
